import { Component, Inject, OnInit } from '@angular/core';
import {
	MSAL_GUARD_CONFIG,
	MsalBroadcastService,
	MsalGuardConfiguration,
	MsalService
} from '@azure/msal-angular';
import {
	AuthenticationResult,
	EventMessage,
	EventType,
	InteractionStatus,
	InteractionType,
	PopupRequest,
	RedirectRequest
} from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { IdTokenClaimsWithPolicyId, b2cPolicies } from '../auth-config';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { TokenService } from 'src/app/core/services/token.service';
import { AlertService } from 'src/app/core/services/alert.service';
import {
	SESSION_STORAGE_REGISTRO_PRODUCTOR_IDENTIDAD_KEY,
	SESSION_STORAGE_REGISTRO_PRODUCTOR_TOKEN_KEY
} from 'src/app/shared/constants/session-storage-keys';
import { RegistroProductorService } from '../registro-productor/registro-productor.service';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
	private readonly _destroying$ = new Subject<void>();

	constructor(
		@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
		private msalAuthService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private tokenService: TokenService,
		private authService: AuthService,
		private router: Router,
		private route: ActivatedRoute,
		private registroProductorService: RegistroProductorService,
		private alertService: AlertService
	) {}

	ngOnInit(): void {
		const registroTokenQuery =
			this.route.snapshot.queryParamMap.get('registroToken');
		if (registroTokenQuery) {
			sessionStorage.setItem(
				SESSION_STORAGE_REGISTRO_PRODUCTOR_TOKEN_KEY,
				registroTokenQuery
			);
		}

		// Ya autenticado + token de registro: vincular sin repetir B2C.
		if (
			this.authService.isLoggedIn() &&
			sessionStorage.getItem(SESSION_STORAGE_REGISTRO_PRODUCTOR_TOKEN_KEY)
		) {
			this.continuarPostLogin();
			return;
		}

		this.msalAuthService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

		/**
		 * You can subscribe to MSAL events as shown below. For more info,
		 * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
		 */
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) =>
						msg.eventType === EventType.ACCOUNT_ADDED ||
						msg.eventType === EventType.ACCOUNT_REMOVED
				)
			)
			.subscribe(() => {
				if (this.msalAuthService.instance.getAllAccounts().length === 0) {
					window.location.pathname = '/';
				}
			});

		this.msalBroadcastService.inProgress$
			.pipe(
				filter(
					(status: InteractionStatus) => status === InteractionStatus.None
				),
				takeUntil(this._destroying$)
			)
			.subscribe(() => {
				this.checkAndSetActiveAccount();
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) =>
						msg.eventType === EventType.LOGIN_SUCCESS ||
						msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
						msg.eventType === EventType.SSO_SILENT_SUCCESS
				),
				takeUntil(this._destroying$)
			)
			.subscribe((result: EventMessage) => {
				const payload = result.payload as AuthenticationResult;
				const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

				if (
					idtoken.acr === b2cPolicies.names.signUpSignIn ||
					idtoken.tfp === b2cPolicies.names.signUpSignIn
				) {
					this.msalAuthService.instance.setActiveAccount(payload.account);
					this.tokenService.saveAccessToken(payload.accessToken);
					this.continuarPostLogin();
				}
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter(
					(msg: EventMessage) =>
						msg.eventType === EventType.LOGIN_FAILURE ||
						msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
				),
				takeUntil(this._destroying$)
			)
			.subscribe((result: EventMessage) => {
				// Checking for the forgot password error. Learn more about B2C error codes at
				// https://learn.microsoft.com/azure/active-directory-b2c/error-codes
				if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
					const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
						authority: b2cPolicies.authorities.resetPassword.authority,
						scopes: []
					};

					this.login(resetPasswordFlowRequest);
				}
			});
	}

	checkAndSetActiveAccount() {
		/**
		 * If no active account set but there are accounts signed in, sets first account to active account
		 * To use active account set here, subscribe to inProgress$ first in your component
		 * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
		 */
		const activeAccount = this.msalAuthService.instance.getActiveAccount();

		if (
			!activeAccount &&
			this.msalAuthService.instance.getAllAccounts().length > 0
		) {
			const accounts = this.msalAuthService.instance.getAllAccounts();
			// add your code for handling multiple accounts here
			this.msalAuthService.instance.setActiveAccount(accounts[0]);
		}
	}

	login(userFlowRequest?: RedirectRequest | PopupRequest) {
		if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
			if (this.msalGuardConfig.authRequest) {
				this.msalAuthService
					.loginPopup({
						...this.msalGuardConfig.authRequest,
						...userFlowRequest
					} as PopupRequest)
					.subscribe((response: AuthenticationResult) => {
						this.msalAuthService.instance.setActiveAccount(response.account);
					});
			} else {
				this.msalAuthService
					.loginPopup(userFlowRequest)
					.subscribe((response: AuthenticationResult) => {
						this.msalAuthService.instance.setActiveAccount(response.account);
					});
			}
		} else {
			if (this.msalGuardConfig.authRequest) {
				this.msalAuthService.loginRedirect({
					...this.msalGuardConfig.authRequest,
					...userFlowRequest
				} as RedirectRequest);
			} else {
				this.msalAuthService.loginRedirect(userFlowRequest);
			}
		}
	}

	private continuarPostLogin(): void {
		const registroToken = sessionStorage.getItem(
			SESSION_STORAGE_REGISTRO_PRODUCTOR_TOKEN_KEY
		);

		if (!registroToken) {
			this.router.navigate(['/']);
			return;
		}

		this.registroProductorService.vincular({ token: registroToken }).subscribe({
			next: () => {
				this.limpiarContextoRegistro();
				this.router.navigate(['/']);
			},
			error: () => {
				this.alertService.open(
					this.alertService.datosError?.errorMessage ||
						'No se pudo vincular la empresa al usuario. El inicio de sesión se completó.',
					'Cerrar'
				);
				this.limpiarContextoRegistro();
				this.router.navigate(['/']);
			}
		});
	}

	private limpiarContextoRegistro(): void {
		sessionStorage.removeItem(SESSION_STORAGE_REGISTRO_PRODUCTOR_TOKEN_KEY);
		sessionStorage.removeItem(SESSION_STORAGE_REGISTRO_PRODUCTOR_IDENTIDAD_KEY);
	}
}
